/**
 * @fileoverview Lightweight in-memory performance event bus (dev-only).
 * @description Aggregates lookup/query/render timing events emitted from
 * `useLookupsQuery`, the EmployeeForm render profiler, and any other
 * `performance.now()` instrumented code path. The DevPerfDashboard
 * subscribes to this bus to display the slowest paths.
 *
 * Events are persisted to `localStorage` (dev only) so the dashboard
 * retains the previous session's timings after a page refresh.
 *
 * Throttling & batching:
 *  - Incoming emits are appended to a pending queue and flushed on the
 *    next animation frame (or a 50ms fallback timer). This collapses
 *    bursts (e.g. rapid form mount/unmount cycles, dozens of lookups
 *    resolving in the same tick) into a single notify + persist pass.
 *  - A per-label rate limit (MAX_PER_LABEL_PER_WINDOW within
 *    LABEL_WINDOW_MS) drops runaway emitters so a single hot path
 *    cannot flood the in-memory ring buffer or localStorage.
 *
 * Disabled in production builds (no-op emit, no retention).
 */

const isDev = import.meta.env.DEV;

export type PerfCategory = 'lookup' | 'render' | 'query' | 'other';

export interface PerfEvent {
  id: number;
  ts: number;        // epoch ms
  label: string;     // e.g. "sections[dept=...]" or "EmployeeForm"
  category: PerfCategory;
  durationMs: number;
  slow: boolean;     // exceeded the category threshold
  context?: string;  // optional source tag (e.g. "Lookups")
}

type Listener = (events: PerfEvent[]) => void;

const MAX_EVENTS = 200;
const STORAGE_KEY = 'lovable.perfBus.events.v1';
const PERSIST_DEBOUNCE_MS = 250;

// Per-label throttle: at most N events per label per rolling window.
const LABEL_WINDOW_MS = 1000;
const MAX_PER_LABEL_PER_WINDOW = 20;

// Batch flush cadence — coalesces bursts within a single frame.
const FLUSH_FALLBACK_MS = 50;

let events: PerfEvent[] = [];
const listeners = new Set<Listener>();
let nextId = 1;
let persistTimer: ReturnType<typeof setTimeout> | null = null;

// Batching state.
let pending: PerfEvent[] = [];
let flushScheduled = false;
let flushRaf: number | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Per-label rate-limit state: label -> timestamps within the active window.
const labelHits = new Map<string, number[]>();
let droppedSinceLastFlush = 0;

function safeStorage(): Storage | null {
  if (!isDev) return null;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function loadFromStorage() {
  const ls = safeStorage();
  if (!ls) return;
  try {
    const raw = ls.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return;
    events = parsed.slice(0, MAX_EVENTS).filter(
      (e): e is PerfEvent =>
        e && typeof e.id === 'number' && typeof e.label === 'string' &&
        typeof e.durationMs === 'number' && typeof e.category === 'string'
    );
    nextId = events.reduce((max, e) => Math.max(max, e.id), 0) + 1;
  } catch {
    /* corrupted — ignore */
  }
}

function schedulePersist() {
  const ls = safeStorage();
  if (!ls) return;
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    try {
      ls.setItem(STORAGE_KEY, JSON.stringify(events));
    } catch {
      /* quota / serialization — ignore */
    }
  }, PERSIST_DEBOUNCE_MS);
}

function notify() {
  const snap = events.slice();
  for (const l of listeners) l(snap);
}

/** True if this label is allowed to emit right now (sliding window). */
function allowLabel(label: string): boolean {
  const now = Date.now();
  const arr = labelHits.get(label);
  if (!arr) {
    labelHits.set(label, [now]);
    return true;
  }
  // Drop timestamps outside the window.
  const cutoff = now - LABEL_WINDOW_MS;
  let i = 0;
  while (i < arr.length && arr[i] < cutoff) i++;
  if (i > 0) arr.splice(0, i);
  if (arr.length >= MAX_PER_LABEL_PER_WINDOW) return false;
  arr.push(now);
  return true;
}

function scheduleFlush() {
  if (flushScheduled) return;
  flushScheduled = true;
  if (typeof requestAnimationFrame !== 'undefined') {
    flushRaf = requestAnimationFrame(flush);
  } else {
    flushTimer = setTimeout(flush, FLUSH_FALLBACK_MS);
  }
  // Safety fallback in case rAF is throttled (background tab).
  if (typeof requestAnimationFrame !== 'undefined') {
    flushTimer = setTimeout(flush, FLUSH_FALLBACK_MS * 4);
  }
}

function flush() {
  flushScheduled = false;
  if (flushRaf !== null && typeof cancelAnimationFrame !== 'undefined') {
    cancelAnimationFrame(flushRaf);
    flushRaf = null;
  }
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pending.length === 0 && droppedSinceLastFlush === 0) return;

  if (pending.length > 0) {
    // Newest-first ordering preserved; pending was appended in arrival order.
    // Prepend the batch reversed so most-recent ends up at index 0.
    for (let i = pending.length - 1; i >= 0; i--) events.unshift(pending[i]);
    if (events.length > MAX_EVENTS) events.length = MAX_EVENTS;
    pending = [];
  }

  if (droppedSinceLastFlush > 0) {
    // Surface throttling once per flush so it's visible in the dashboard
    // without itself being subject to the per-label limit explosion.
    const evt: PerfEvent = {
      id: nextId++,
      ts: Date.now(),
      label: `perfBus throttled (${droppedSinceLastFlush} dropped)`,
      category: 'other',
      durationMs: 0,
      slow: false,
      context: 'perfBus',
    };
    events.unshift(evt);
    if (events.length > MAX_EVENTS) events.length = MAX_EVENTS;
    droppedSinceLastFlush = 0;
  }

  notify();
  schedulePersist();
}

// Hydrate immediately so subscribers see prior session's events.
loadFromStorage();

export const perfBus = {
  emit(input: Omit<PerfEvent, 'id' | 'ts'>): void {
    if (!isDev) return;
    if (!allowLabel(input.label)) {
      droppedSinceLastFlush++;
      scheduleFlush();
      return;
    }
    const evt: PerfEvent = { id: nextId++, ts: Date.now(), ...input };
    pending.push(evt);
    scheduleFlush();
  },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    fn(events.slice());
    return () => { listeners.delete(fn); };
  },
  clear(): void {
    events = [];
    pending = [];
    labelHits.clear();
    droppedSinceLastFlush = 0;
    const ls = safeStorage();
    if (ls) {
      try { ls.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    }
    notify();
  },
  snapshot(): PerfEvent[] {
    return events.slice();
  },
};

/** Aggregate events by label: count, avg, max, p95-ish (max of last N). */
export interface PerfAggregate {
  label: string;
  category: PerfCategory;
  count: number;
  avgMs: number;
  maxMs: number;
  lastMs: number;
  slowCount: number;
}

export function aggregate(events: PerfEvent[]): PerfAggregate[] {
  const map = new Map<string, PerfAggregate>();
  for (const e of events) {
    const key = `${e.category}::${e.label}`;
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
      existing.avgMs = ((existing.avgMs * (existing.count - 1)) + e.durationMs) / existing.count;
      existing.maxMs = Math.max(existing.maxMs, e.durationMs);
      existing.lastMs = e.durationMs;
      if (e.slow) existing.slowCount += 1;
    } else {
      map.set(key, {
        label: e.label,
        category: e.category,
        count: 1,
        avgMs: e.durationMs,
        maxMs: e.durationMs,
        lastMs: e.durationMs,
        slowCount: e.slow ? 1 : 0,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.maxMs - a.maxMs);
}
